import { showAlertCallout } from "@/shared/alerts";
import { ERROR_DISPLAY_TYPES } from "@/shared/utils/errors/error.types";
import {
  getTranslationKeyAndDisplayTypes,
  hasErrorDisplayType,
} from "@/shared/utils/errors/get-http-error-type";
import { showHttpErrorAlertCallout } from "@/shared/utils/show-http-error-alert-callout";
import type {
  UpdateUserGroupRequestDto,
  UpdateUserGroupResponseDto,
} from "@/spz-api/model";
import {
  getGetUserGroupListPaginatedQueryKey,
  updateUserGroup,
  useCreateUserGroup,
} from "@/spz-api/user-groups/user-groups";
import { useGetUserListPaginated } from "@/spz-api/user/user";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TreeNode } from "@spz/ui/components/checkbox-tree";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { USER_GROUPS_FORM_DEFAULT_VALUES } from "../forms/constants/user-groups-form.constants";
import {
  type UserGroupsFormSchemaValues,
  userGroupsFormSchema,
} from "../forms/schemas/user-groups-form.schema";

interface UseUserGroupsFormProps {
  initialValues?: UserGroupsFormSchemaValues;
  onSuccess?: () => void;
  groupId?: string;
}

export const useUserGroupsForm = ({
  initialValues,
  onSuccess,
  groupId,
}: UseUserGroupsFormProps) => {
  const defaultValues: UserGroupsFormSchemaValues = {
    ...USER_GROUPS_FORM_DEFAULT_VALUES,
    ...initialValues,
  };
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const userGroupsSchema = userGroupsFormSchema(t);

  const { data: users, isLoading: isLoadingUsers } = useGetUserListPaginated({
    page: 1,
    pageSize: -1,
  });

  const form = useForm<UserGroupsFormSchemaValues>({
    resolver: zodResolver(userGroupsSchema),
    defaultValues,
    mode: "onBlur",
  });

  const createUserGroupsMutation = useCreateUserGroup({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("create_group_success"),
        });

        queryClient.invalidateQueries({
          queryKey: getGetUserGroupListPaginatedQueryKey({}),
        });

        onSuccess?.();
      },
      onError: (error: unknown) => {
        const { translationKey, displayTypes } =
          getTranslationKeyAndDisplayTypes(error);
        const message = t(translationKey);
        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.FORM)) {
          form.setError("name", {
            type: "manual",
            message,
          });
        }

        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
          showHttpErrorAlertCallout({
            error: message,
          });
          queryClient.invalidateQueries({
            queryKey: getGetUserGroupListPaginatedQueryKey({}),
          });
        }
      },
    },
  });
  type UpdateUserGroupMutationVariables = {
    id: string;
    data: UpdateUserGroupRequestDto;
    headers?: Record<string, string>;
  };

  const updateUserGroupMutation = useMutation<
    UpdateUserGroupResponseDto,
    unknown,
    UpdateUserGroupMutationVariables
  >({
    mutationFn: async ({ id, data, headers }) => {
      return updateUserGroup(id, data, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      showAlertCallout({
        type: "success",
        title: t("update_group_success"),
      });

      queryClient.invalidateQueries({
        queryKey: getGetUserGroupListPaginatedQueryKey({}),
      });

      onSuccess?.();
    },
    onError: (error: unknown) => {
      const { translationKey, displayTypes } =
        getTranslationKeyAndDisplayTypes(error);
      const message = t(translationKey);
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.FORM)) {
        form.setError("name", {
          type: "manual",
          message,
        });
      }
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
        showHttpErrorAlertCallout({
          error: message,
        });
        queryClient.invalidateQueries({
          queryKey: getGetUserGroupListPaginatedQueryKey({}),
        });
      }
    },
  });

  const onSubmit = async (data: UserGroupsFormSchemaValues) => {
    if (groupId) {
      const updatedAt = form.getValues().updatedAt;
      updateUserGroupMutation.mutate({
        id: groupId,
        data: {
          name: data.name.trim(),
          description: data.description,
          userIds: data.users ?? [],
        },
        headers: updatedAt ? { "if-match": updatedAt } : undefined,
      });
    } else {
      createUserGroupsMutation.mutate({
        data: {
          name: data.name,
          description: data.description,
          userIds: data.users ?? [],
        },
      });
    }
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: form.formState.isSubmitting,
    users: {
      options: (users?.data ?? []).map<TreeNode>((item) => ({
        id: String(item.id),
        label: `${item.firstName} ${item.lastName}`,
        tooltip: item.email,
      })),
      loading: isLoadingUsers,
    },
  };
};
